<template>
  <v-card flat>
    <!-- Скелетон для загрузки -->
    <template v-if="isLoading">
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-card outlined>
              <v-card-text class="form-wrapper">
                <div class="form-column">
                  <div v-for="i in 8" :key="i" class="form-row">
                    <div class="form-label">
                      <v-skeleton-loader type="text" width="100" />
                    </div>
                    <v-skeleton-loader type="text" width="100%" />
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </template>

    <!-- Ошибка -->
    <template v-else-if="hasError">
      <v-card-text>
        <v-alert
          type="error"
          variant="tonal"
          closable
          @click:close="hasError = false"
        >
          {{ errorMessage || "Ошибка загрузки данных" }}
        </v-alert>
      </v-card-text>
    </template>

    <!-- Основной контент -->
    <template v-else>
      <v-row>
        <v-col v-for="(parent, index) in parents" :key="index" cols="12">
          <v-card outlined>
            <v-card-text class="form-wrapper">
              <div class="form-column">
                <!-- Кнопка удаления -->
                <div class="d-flex justify-end mb-2">
                  <v-btn
                    v-if="parents.length > 1"
                    icon="mdi-delete"
                    color="red"
                    variant="text"
                    @click="removeParent(index)"
                  />
                </div>

                <div class="form-row">
                  <div class="form-label">Фамилия</div>
                  <v-text-field
                    v-model="parent.lastname"
                    hide-details
                    variant="outlined"
                    density="compact"
                    @input="
                      onFieldUpdate(index, 'lastname', $event.target.value)
                    "
                  />
                </div>

                <div class="form-row">
                  <div class="form-label">Имя</div>
                  <v-text-field
                    v-model="parent.firstname"
                    hide-details
                    variant="outlined"
                    density="compact"
                    @input="
                      onFieldUpdate(index, 'firstname', $event.target.value)
                    "
                  />
                </div>

                <div class="form-row">
                  <div class="form-label">Отчество</div>
                  <v-text-field
                    v-model="parent.patronymic"
                    hide-details
                    variant="outlined"
                    density="compact"
                    @input="
                      onFieldUpdate(index, 'patronymic', $event.target.value)
                    "
                  />
                </div>

                <div class="form-row">
                  <div class="form-label">Статус</div>
                  <v-autocomplete
                    v-model="parent.type"
                    :items="representative"
                    item-title="name"
                    item-value="code"
                    hide-details
                    variant="outlined"
                    density="compact"
                    @update:modelValue="onFieldUpdate(index, 'type', $event)"
                  />
                </div>

                <div class="form-row">
                  <div class="form-label">Дата рождения</div>
                  <v-text-field
                    v-model="parent.birthday"
                    type="date"
                    hide-details
                    variant="outlined"
                    density="compact"
                    @update:modelValue="
                      onFieldUpdate(index, 'birthday', $event)
                    "
                  />
                </div>

                <div class="form-row">
                  <div class="form-label">Пол</div>
                  <v-radio-group
                    v-model="parent.gender"
                    inline
                    hide-details
                    @update:modelValue="onFieldUpdate(index, 'gender', $event)"
                  >
                    <v-radio label="мужской" :value="1" />
                    <v-radio label="женский" :value="0" />
                  </v-radio-group>
                </div>

                <div class="form-row">
                  <div class="form-label">Телефон</div>
                  <v-text-field
                    v-model="parent.phone"
                    hide-details
                    variant="outlined"
                    density="compact"
                    @input="onFieldUpdate(index, 'phone', $event.target.value)"
                  />
                </div>

                <div class="form-row">
                  <div class="form-label">Email</div>
                  <v-text-field
                    v-model="parent.email"
                    variant="outlined"
                    density="compact"
                    :rules="emailRules"
                    type="email"
                    placeholder="example@mail.com"
                    @update:modelValue="onFieldUpdate(index, 'email', $event)"
                  />
                </div>

                <div class="form-row">
                  <div class="form-label">СНИЛС</div>
                  <v-text-field
                    v-model="parent.snils"
                    hide-details
                    variant="outlined"
                    density="compact"
                    @input="onFieldUpdate(index, 'snils', $event.target.value)"
                  />
                </div>

                <v-divider class="my-2"></v-divider>

                <div class="form-row">
                  <div class="form-label">Паспортные данные</div>
                  <passport-fields
                    v-model="parent.passport"
                    density="compact"
                    :hide-details="false"
                    @update:modelValue="onPassportUpdate(index, $event)"
                  />
                </div>

                <v-divider class="my-2"></v-divider>

                <div class="form-row">
                  <div class="form-label">Место работы</div>
                  <v-text-field
                    v-model="parent.work"
                    hide-details
                    variant="outlined"
                    density="compact"
                    @input="onFieldUpdate(index, 'work', $event.target.value)"
                  />
                </div>

                <div class="form-row">
                  <div class="form-label">Адрес регистрации</div>
                  <addressDetails
                    class="details"
                    :addressId="parent.reg_addr_id"
                    @update:addressId="
                      onAddressUpdate(index, 'reg_addr_id', $event)
                    "
                  />
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mt-4">
        <v-col cols="12" class="d-flex justify-center">
          <v-btn
            v-if="parents.length < 2"
            color="primary"
            variant="outlined"
            @click="addParent"
          >
            Добавить родителя
          </v-btn>
        </v-col>
      </v-row>
    </template>
  </v-card>
</template>

<script>
import addressDetails from "./addressDetails.vue";
import PassportFields from "moodleDir/procollege/lib/vue/passport.vue";

export default {
  name: "PersonalParents",

  components: {
    addressDetails,
    PassportFields,
  },

  props: {
    student: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      parents: [],
      representative: [],
      isLoading: false,
      hasError: false,
      errorMessage: null,

      emailRules: [(v) => !v || /.+@.+\..+/.test(v) || "Некорректный email"],
    };
  },

  watch: {
    student: {
      handler(newVal) {
        if (newVal && newVal.id) {
          this.getParents();
        } else {
          this.initEmptyParents();
        }
      },
      immediate: true,
    },
  },

  created() {
    this.loadDirectory();
  },

  methods: {
    createParent() {
      return {
        lastname: "",
        firstname: "",
        patronymic: "",
        type: null,
        birthday: "",
        gender: 1,
        phone: "",
        email: "",
        snils: "",
        work: "",
        reg_addr_id: null,
        passport: {},
      };
    },

    initEmptyParents() {
      if (this.parents.length === 0) {
        this.parents = [this.createParent()];
        // НЕ вызываем emitUpdate() здесь, чтобы не триггерить изменения
      }
    },

    addParent() {
      if (this.parents.length < 2) {
        const newParent = this.createParent();
        this.parents.push(newParent);
        this.emitUpdate();
      }
    },

    removeParent(index) {
      this.parents.splice(index, 1);
      if (this.parents.length === 0) {
        this.parents.push(this.createParent());
      }
      this.emitUpdate();
    },

    loadDirectory() {
      this.isLoading = true;

      this.axios
        .post(
          this.$model.wwwroot +
            "/procollege/core_procollege/helpers/DirectoryLoader/Load",
          new URLSearchParams({
            directory: JSON.stringify(["representative"]),
          })
        )
        .then((res) => {
          this.representative = [];
          for (let i in res.data.representative) {
            this.representative.push({
              code: res.data.representative[i].code,
              name: res.data.representative[i].name,
            });
          }
        })
        .catch((err) => {
          console.error("Ошибка загрузки справочника:", err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    getParents() {
      if (this.student && this.student.id) {
        this.isLoading = true;
        this.hasError = false;
        this.errorMessage = null;

        this.axios
          .post(
            this.$model.wwwroot +
              "/procollege/manage_groups/secretariat/studentCard/getParents",
            new URLSearchParams({
              personal_id: this.student.id,
            })
          )
          .then((res) => {
            if (res.data && res.data.length > 0) {
              this.parents = res.data.map((parent) => ({
                ...this.createParent(),
                ...parent,
              }));
            } else {
              this.parents = [this.createParent()];
            }
          })
          .catch((err) => {
            this.hasError = true;
            this.errorMessage =
              err.message || "Ошибка загрузки данных родителей";
            this.parents = [this.createParent()];
            console.error("Ошибка загрузки родителей:", err);
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },

    onFieldUpdate(index, field, value) {
      if (this.parents[index]) {
        this.parents[index][field] = value;
        this.emitUpdate();
      }
    },

    onPassportUpdate(index, value) {
      if (this.parents[index]) {
        this.parents[index].passport = value;
        this.emitUpdate();
      }
    },

    onAddressUpdate(index, field, value) {
      if (this.parents[index]) {
        this.parents[index][field] = value;
        this.emitUpdate();
      }
    },

    emitUpdate() {
      // Отправляем только массив родителей
      this.$emit("update:parents", this.parents);
    },
  },
};
</script>

<style scoped>
.details {
  flex: 1;
}
.form-column {
  width: 100%;
  max-width: 700px;
}
::v-deep(.v-checkbox .v-selection-control__input),
:deep(.v-radio .v-selection-control__input) {
  color: rgba(5, 55, 135, 0.8) !important;
}
</style>